import { getConfig } from "@/features/branding";
import { HacsOmUploadData } from "@/features/hacs";
import { RemoteNewRunModal, RemoteNewRunModalProps } from "@/features/remote_jobs";
import { PortfolioExportErrorAlert } from "@/tools/aggregate/portfolio-export/components/PortfolioExportErrorAlert";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { Spin } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SeveritiesJobSchema } from "../types";
import { createSeveritiesRun } from "../utils/supabase";
import { parseSeveritiesCsv, SeveritiesRow } from "../utils/validation";
import { SeveritiesUploadForm } from "./SeveritiesUploadForm";
import { SeveritiesReviewValues, SeveritiesUploadReview } from "./SeveritiesUploadReview";
type Status = "INITIAL" | "VALIDATING" | "IN_REVIEW" | "CREATING";
export const SeveritiesNewRunModal = (props: RemoteNewRunModalProps<SeveritiesJobSchema>) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.severities.newRunModal"
  });
  const [keys] = useKeys();
  const [status, setStatus] = useState<Status>("INITIAL");
  const [errors, setErrors] = useState<string[]>([]);
  const [upload, setUpload] = useState<{
    fileName: string;
    rows: SeveritiesRow[];
  }>();
  const [uploadedCount, setUploadedCount] = useState(0);
  const {
    defaultMetadata,
    portfolioExport
  } = getConfig();
  const maxRows = portfolioExport.maxAddresses;
  const validateFile = async ({
    file
  }: HacsOmUploadData) => {
    setErrors([]);
    setStatus("VALIDATING");
    const result = await parseSeveritiesCsv(file, maxRows);
    if (result.status === "error") {
      setErrors(result.messages);
      setStatus("INITIAL");
      return;
    }
    setUpload({
      fileName: file.name,
      rows: result.rows
    });
    setStatus("IN_REVIEW");
  };
  const startRun = async ({
    name,
    isPrivate,
    groupId,
    anomalyManagementRp,
    defences
  }: SeveritiesReviewValues) => {
    if (!upload) return;
    const publicKey = isPrivate ? keys.user?.keyPair.publicKey : keys.org?.keyPair.publicKey;
    if (!publicKey) {
      setErrors(["Your encryption keys aren't loaded. Please refresh your browser and try again."]);
      return;
    }
    setErrors([]);
    setUploadedCount(0);
    setStatus("CREATING");
    try {
      await createSeveritiesRun({
        name: name.trim() || removeFileExtension(upload.fileName),
        rows: upload.rows,
        settings: {
          anomaly_management_rp: anomalyManagementRp ? defaultMetadata.anomalyManagementRp : defaultMetadata.anomalyManagementRpDisabled,
          defences: defences ? defaultMetadata.defences : defaultMetadata.defencesDisabled
        },
        isPrivate,
        groupId,
        publicKey,
        onUploaded: setUploadedCount
      });
      props.onCancel?.({} as any);
    } catch (err) {
      console.error("Failed to create Severities run:", err);
      setErrors(["An error occurred while uploading your locations. This run will not deduct from your quota. Please try again, or contact XDI Support with the time and date of the run."]);
      setStatus("IN_REVIEW");
    }
  };
  const isCreating = status === "CREATING";
  return <RemoteNewRunModal title={status === "IN_REVIEW" || isCreating ? t("review.title") : t("upload.title")} mask={{
    closable: status === "INITIAL"
  }} width={700} {...props} closable={!isCreating} keyboard={!isCreating} data-sentry-element="RemoteNewRunModal" data-sentry-component="SeveritiesNewRunModal" data-sentry-source-file="SeveritiesNewRunModal.tsx">
      <div className="pt-2">
        {!!errors.length && <PortfolioExportErrorAlert errors={errors} onClose={() => setErrors([])} />}

        {status === "INITIAL" && <SeveritiesUploadForm onFileSelected={validateFile} maxRows={maxRows} />}

        {(status === "VALIDATING" || isCreating) && <div className="flex flex-col items-center gap-3 py-12 text-sm text-gray-500">
            <Spin />
            {isCreating && upload ? `Uploading ${uploadedCount.toLocaleString()} / ${upload.rows.length.toLocaleString()} locations` : "Checking your file"}
          </div>}

        {/* Stays mounted while creating, so a failed upload returns to the user's edits. */}
        {(status === "IN_REVIEW" || isCreating) && upload && <div hidden={isCreating}>
            <SeveritiesUploadReview fileName={upload.fileName} rowCount={upload.rows.length} onBack={() => {
          setUpload(undefined);
          setStatus("INITIAL");
        }} onSubmit={startRun} />
          </div>}
      </div>
    </RemoteNewRunModal>;
};